import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Edit } from 'lucide-react';
import { toast } from 'sonner';

const EditableTask = ({ task, onTaskUpdate, dayNumber }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(task.task_description);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (editedDescription.trim() === task.task_description) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onTaskUpdate(task.id, { task_description: editedDescription.trim() });
      setIsEditing(false);
      toast.success('Task updated successfully');
    } catch {
      toast.error('Failed to update task');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedDescription(task.task_description);
    setIsEditing(false);
  };

  const handleToggleComplete = async (checked) => {
    try {
      await onTaskUpdate(task.id, { completed: checked });
    } catch {
      toast.error('Failed to update task');
    }
  };

  return (
    <div className="flex items-start space-x-3 p-3 bg-white rounded border">
      <Checkbox checked={task.completed} onCheckedChange={handleToggleComplete} className="mt-1" />
      <div className="flex-1">
        {isEditing ? (
          <div className="space-y-2">
            <Textarea value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} className="text-sm" rows={2} />
            <div className="flex space-x-2">
              <Button size="sm" onClick={handleSave} disabled={isSaving} className="bg-justb-secondary hover:bg-justb-secondary/90">
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className={`text-sm ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
              <span className="font-medium text-justb-secondary">Day {dayNumber}:</span> {task.task_description}
            </p>
            <p className="text-xs text-gray-500 mt-1">{new Date(task.date).toLocaleDateString()}</p>
          </div>
        )}
      </div>
      <div className="flex items-center space-x-2">
        {task.time_spent && <Badge variant="outline" className="text-xs">{task.time_spent}m</Badge>}
        {!isEditing && (
          <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} className="text-justb-primary hover:text-justb-primary/80">
            <Edit className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default EditableTask;
